import { create } from 'zustand';
import { crmApi, type CrmClient } from '../services/api';

/**
 * Which of our companies the CRM session is selling as.
 *
 * A seller is a `parent` account — Heuristic Systems or West42 — distinct from
 * the partner (the reseller a deal is sold THROUGH). Picked once at the top of
 * any CRM page and applied across all of them: new opportunities and quotes are
 * stamped with it, and the lists show that company's deals. '' is "all
 * companies", which stamps nothing.
 *
 * Remembered per browser, like the rail group: a person who sells for West42
 * all day should not re-pick it on every page.
 */
interface CrmSellerState {
  sellerOrgId: string;
  sellers: CrmClient[];
  loaded: boolean;
  setSeller: (orgId: string) => void;
  loadSellers: () => Promise<void>;
  /** The seller's display name, or '' when unknown. */
  nameOf: (orgId?: string) => string;
}

const KEY = 'crmSellerOrgId';

function remembered(): string {
  try { return localStorage.getItem(KEY) || ''; } catch { return ''; }
}

export const useCrmSellerStore = create<CrmSellerState>((set, get) => ({
  sellerOrgId: remembered(),
  sellers: [],
  loaded: false,

  setSeller: (orgId) => {
    try {
      if (orgId) localStorage.setItem(KEY, orgId);
      else localStorage.removeItem(KEY);
    } catch { /* private window: the choice lasts for this page only */ }
    set({ sellerOrgId: orgId });
  },

  loadSellers: async () => {
    if (get().loaded) return;
    try {
      const { clients } = await crmApi.listClients(undefined, 'parent');
      const sellers = (clients || []).filter((c) => c.orgId);
      set({ sellers, loaded: true });
      // A remembered seller that has since been archived or retyped would
      // silently stamp every new deal with a company that cannot sell.
      const current = get().sellerOrgId;
      if (current && !sellers.some((s) => s.orgId === current)) get().setSeller('');
    } catch {
      set({ loaded: true });
    }
  },

  nameOf: (orgId) => {
    if (!orgId) return '';
    return get().sellers.find((s) => s.orgId === orgId)?.name || orgId;
  },
}));
